import { KnowledgeDocumentVersion } from '@/types/knowledge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Eye, GitCompare } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface VersionHistoryProps {
  versions: KnowledgeDocumentVersion[];
  documentId: string;
  selectedVersions: string[];
  onToggleVersion: (versionId: string) => void;
}

export function VersionHistory({
  versions,
  documentId,
  selectedVersions,
  onToggleVersion
}: VersionHistoryProps) {
  const navigate = useNavigate();

  return (
    <div className="overflow-x-auto">
      <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-12">
            <span className="sr-only">Selecionar</span>
          </TableHead>
          <TableHead>Versão</TableHead>
          <TableHead>Data</TableHead>
          <TableHead>Protocolo</TableHead>
          <TableHead>Tipo</TableHead>
          <TableHead>Motivo</TableHead>
          <TableHead className="w-20">Ações</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {versions.map((version) => (
          <TableRow key={version.id}>
            <TableCell>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      <Checkbox
                        checked={selectedVersions.includes(version.id)}
                        onCheckedChange={() => onToggleVersion(version.id)}
                        aria-label={`Selecionar versão ${version.version}`}
                      />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    {selectedVersions.includes(version.id)
                      ? 'Clique para desmarcar'
                      : 'Selecione 2 versões para comparar'}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </TableCell>
            <TableCell>
              <Badge variant="outline">v{version.version}</Badge>
            </TableCell>
            <TableCell>
              {format(new Date(version.createdAt), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
            </TableCell>
            <TableCell className="font-mono text-sm">
              {version.sourceProtocol}
            </TableCell>
            <TableCell>
              <Badge variant={version.editedByUserId ? 'default' : 'secondary'}>
                {version.editedByUserId ? 'Manual' : 'Automático'}
              </Badge>
            </TableCell>
            <TableCell className="text-sm text-muted-foreground max-w-xs truncate">
              {version.editReason || '-'}
            </TableCell>
            <TableCell>
              <div className="flex items-center gap-1">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/knowledge/${documentId}/versions/${version.id}`)}
                      >
                        <Eye className="h-4 w-4" />
                        <span className="sr-only">Ver versão</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Ver conteúdo desta versão</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                {/* Botão comparar com anterior - só aparece se não for a primeira versão */}
                {version.version > 1 && (() => {
                  const previousVersion = versions.find(v => v.version === version.version - 1);
                  if (!previousVersion) return null;
                  return (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/knowledge/${documentId}/versions/compare?v1=${previousVersion.id}&v2=${version.id}`)}
                          >
                            <GitCompare className="h-4 w-4" />
                            <span className="sr-only">Comparar com versão anterior</span>
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Comparar com v{previousVersion.version}</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  );
                })()}
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
    </div>
  );
}
